import { reactive } from 'vue';

import type { ToastInput, ToastItem } from './types';

const state = reactive({
  toasts: [] as ToastItem[],
});

let counter = 0;
function nextId(): string {
  counter += 1;
  return `t-${Date.now().toString(36)}-${counter}`;
}

function push(input: ToastInput): ToastItem {
  const item: ToastItem = {
    id: nextId(),
    title: input.title,
    description: input.description,
    variant: input.variant ?? 'info',
    duration: input.duration ?? 5000,
    icon: input.icon,
    action: input.action,
    createdAt: Date.now(),
  };
  state.toasts.push(item);
  if (item.duration > 0) {
    setTimeout(() => dismiss(item.id), item.duration);
  }
  return item;
}

function dismiss(id: string): void {
  const idx = state.toasts.findIndex((t) => t.id === id);
  if (idx >= 0) state.toasts.splice(idx, 1);
}

function clear(): void {
  state.toasts.splice(0, state.toasts.length);
}

export interface UseToastApi {
  toasts: ToastItem[];
  show: (_input: ToastInput) => ToastItem;
  info: (_input: Omit<ToastInput, 'variant'>) => ToastItem;
  success: (_input: Omit<ToastInput, 'variant'>) => ToastItem;
  warning: (_input: Omit<ToastInput, 'variant'>) => ToastItem;
  danger: (_input: Omit<ToastInput, 'variant'>) => ToastItem;
  dismiss: (_id: string) => void;
  clear: () => void;
}

export function useToast(): UseToastApi {
  return {
    toasts: state.toasts,
    show: push,
    info: (input) => push({ ...input, variant: 'info' }),
    success: (input) => push({ ...input, variant: 'success' }),
    warning: (input) => push({ ...input, variant: 'warning' }),
    danger: (input) => push({ ...input, variant: 'danger' }),
    dismiss,
    clear,
  };
}
